import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock, Send, ShieldAlert, UserRound } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Button from "../components/Button";
import PageShell from "../components/PageShell";
import RatingInput from "../components/RatingInput";
import StatusBanner from "../components/StatusBanner";
import { getCurrentAppUser, supabase } from "../lib/supabase";

const initialState = {
  isLoading: true,
  user: null,
  profile: null,
  mentee: null,
  cycle: null,
  existingFeedback: null,
  historicalFeedbacks: [],
  totalCycles: 0,
  error: null,
};

export default function Dashboard() {
  const [state, setState] = useState(initialState);
  const [ratings, setRatings] = useState({ q1_rating: "", q2_rating: "", q3_rating: "", q4_rating: "", q5_rating: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const canSubmit = useMemo(
    () => ratings.q1_rating && ratings.q2_rating && ratings.q3_rating && ratings.q4_rating && ratings.q5_rating && state.cycle && state.mentee,
    [ratings, state.cycle, state.mentee],
  );
  const completedCount = Object.values(ratings).filter(Boolean).length;
  const progressPercent = (completedCount / 5) * 100;

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setState(initialState);

    const { user, profile, error: profileError } = await getCurrentAppUser();

    if (profileError || !profile) {
      setState({
        ...initialState,
        isLoading: false,
        error: "Your account could not be loaded.",
      });
      return;
    }

    if (profile.role === "admin") {
      window.location.replace("/mentorship/admin");
      return;
    }

    const { data: mentee, error: menteeError } = await supabase
      .from("mentees")
      .select("id, name, roll_number, mentor_id, session_id, mentors(name, roll_number, email, phone_number)")
      .eq("id", profile.id)
      .maybeSingle();

    if (menteeError || !mentee) {
      setState({
        ...initialState,
        isLoading: false,
        profile,
        error: "No mentee profile is linked with this account.",
      });
      return;
    }

    const { data: historicalFeedbacks, error: historicalError } = await supabase
      .from("feedbacks")
      .select("id, q1_rating, q2_rating, q3_rating, q4_rating, q5_rating, cycle_id, feedback_cycles(cycle_name)")
      .eq("mentee_id", mentee.id)
      .order("created_at", { ascending: true });

    // Count all feedback cycles that have opened so far (active + closed) for this session
    const now = new Date().toISOString();
    const { count: openedCyclesCount } = await supabase
      .from("feedback_cycles")
      .select("id", { count: "exact", head: true })
      .eq("session_id", mentee.session_id)
      .lte("opens_at", now);

    const totalCycles = Math.max(openedCyclesCount || 0, (historicalFeedbacks || []).length);

    const { data: cycle, error: cycleError } = await supabase
      .from("feedback_cycles")
      .select("id, cycle_name, opens_at, closes_at, session_id")
      .eq("session_id", mentee.session_id)
      .lte("opens_at", now)
      .gte("closes_at", now)
      .order("closes_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (cycleError) {
      setState({
        ...initialState,
        isLoading: false,
        user,
        profile,
        mentee,
        historicalFeedbacks: historicalFeedbacks || [],
        totalCycles: totalCycles || 0,
        error: "Could not check the current feedback cycle.",
      });
      return;
    }

    if (!cycle) {
      setState({
        ...initialState,
        isLoading: false,
        user,
        profile,
        mentee,
        historicalFeedbacks: historicalFeedbacks || [],
        totalCycles: totalCycles || 0,
      });
      return;
    }

    const { data: existingFeedback, error: feedbackError } = await supabase
      .from("feedbacks")
      .select("id, created_at")
      .eq("mentee_id", mentee.id)
      .eq("cycle_id", cycle.id)
      .maybeSingle();

    setState({
      isLoading: false,
      user,
      profile,
      mentee,
      cycle,
      existingFeedback: feedbackError ? null : existingFeedback,
      historicalFeedbacks: historicalFeedbacks || [],
      totalCycles: totalCycles || 0,
      error: feedbackError
        ? "Could not check your previous submission status."
        : null,
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitError(null);

    if (!canSubmit) return;

    setIsSubmitting(true);

    const { error } = await supabase.from("feedbacks").insert({
      mentee_id: state.mentee.id,
      mentor_id: state.mentee.mentor_id,
      cycle_id: state.cycle.id,
      q1_rating: Number(ratings.q1_rating),
      q2_rating: Number(ratings.q2_rating),
      q3_rating: Number(ratings.q3_rating),
      q4_rating: Number(ratings.q4_rating),
      q5_rating: Number(ratings.q5_rating),
    });

    setIsSubmitting(false);

    if (error) {
      setSubmitError(error.message);
      return;
    }

    await loadDashboard();
  }

  /* ---- inline style helpers ---- */
  const card = {
    borderRadius: "16px",
    border: "1px solid #dfe8ee",
    background: "#fff",
    padding: "20px",
    boxShadow: "0 16px 35px rgba(36,55,70,0.08)",
  };
  const label = {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    color: "#64748b",
  };

  const chartData = useMemo(() => {
    if (!state.historicalFeedbacks) return [];
    
    return state.historicalFeedbacks.map((fb) => {
      // Calculate average rating across all questions that are present
      const ratings = [fb.q1_rating, fb.q2_rating, fb.q3_rating, fb.q4_rating, fb.q5_rating].filter(r => r !== null && r !== undefined);
      const avg = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
      return {
        name: fb.feedback_cycles?.cycle_name || `Cycle ${fb.cycle_id}`,
        rating: Number(avg.toFixed(1))
      };
    });
  }, [state.historicalFeedbacks]);

  return (
    <PageShell
      eyebrow="Mentorship Feedback"
      title={
        state.mentee?.name ? `Welcome back, ${state.mentee.name}!` : "Mentorship Dashboard"
      }
    >
      {state.isLoading ? (
        <div style={{ ...card, fontSize: "14px", color: "#475569" }}>
          Loading your mentorship dashboard...
        </div>
      ) : null}

      {!state.isLoading && state.error ? (
        <StatusBanner icon={ShieldAlert} tone="red">
          {state.error}
        </StatusBanner>
      ) : null}



      {!state.isLoading && state.mentee ? (
        <div className="mentorship-dashboard-grid">
          <aside style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Mentee Card */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <img
                  src={state.user?.user_metadata?.avatar_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(state.mentee.name || "M")}
                  alt="Profile"
                  referrerPolicy="no-referrer"
                  style={{
                    height: "64px",
                    width: "64px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #028D93"
                  }}
                />
                <div>
                  <p style={label}>Mentee</p>
                  <h2 style={{ marginTop: "4px", fontSize: "20px", fontWeight: 700, color: "#00293A" }}>
                    {state.mentee.name}
                  </h2>
                  <p style={{ fontSize: "14px", color: "#64748b" }}>
                    {state.mentee.roll_number}
                  </p>
                </div>
              </div>
            </div>

            {/* Mentor Card */}
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    height: "64px",
                    width: "64px",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    background: "#d9f3f2",
                    color: "#028D93",
                  }}
                >
                  <UserRound style={{ width: "36px", height: "36px" }} />
                </div>
                <div>
                  <p style={label}>Mentor</p>
                  <h2 style={{ marginTop: "4px", fontSize: "20px", fontWeight: 700, color: "#00293A" }}>
                    {state.mentee.mentors?.name ?? "Mentor"}
                  </h2>
                  <p style={{ fontSize: "14px", color: "#64748b" }}>
                    {state.mentee.mentors?.roll_number ?? "Roll number unavailable"}
                  </p>
                </div>
              </div>

              {state.mentee.mentors?.email || state.mentee.mentors?.phone_number ? (
              <div
                style={{
                  marginTop: "24px",
                  borderRadius: "12px",
                  background: "#f5f8fb",
                  padding: "16px",
                }}
              >
                <p style={label}>Contact Info</p>
                {state.mentee.mentors?.email ? (
                  <p style={{ marginTop: "8px", fontSize: "14px", fontWeight: 600, color: "#00293A" }}>
                    {state.mentee.mentors.email}
                  </p>
                ) : null}
                {state.mentee.mentors?.phone_number ? (
                  <p style={{ marginTop: "4px", fontSize: "14px", color: "#64748b" }}>
                    {state.mentee.mentors.phone_number}
                  </p>
                ) : null}
              </div>
              ) : null}

              <div style={{ marginTop: "16px", textAlign: "right" }}>
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=info.swgiitkgp@gmail.com&su=${encodeURIComponent(`Mentorship Portal Issue - ${state.mentee.name} (${state.mentee.roll_number})`)}&body=${encodeURIComponent("Hi SWG,\n\nI am facing an issue with my mentor details on the portal.\n\nIssue Details:\n")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "13px",
                    color: "#64748b",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onMouseOver={(e) => (e.target.style.color = "#028D93")}
                  onMouseOut={(e) => (e.target.style.color = "#64748b")}
                >
                  Incorrect details? Report an issue
                </a>
              </div>

              <div
                style={{
                  marginTop: "20px",
                  borderRadius: "12px",
                  border: "1px solid #dfe8ee",
                  padding: "16px",
                }}
              >
                <div
                  style={{
                    marginBottom: "8px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    alignItems: "flex-start",
                    fontSize: "14px",
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#00293A" }}>Feedbacks Given</span>
                  <span style={{ fontWeight: 700, color: "#028D93" }}>{state.historicalFeedbacks.length} out of {state.totalCycles} cycles</span>
                </div>
              </div>
            </div>
          </aside>

          <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {!state.cycle ? (
              <StatusBanner icon={Clock} tone="amber">
                No feedback cycle is active right now.
              </StatusBanner>
            ) : null}

            {state.cycle && state.existingFeedback ? (
              <div style={card}>
                <div style={{ marginBottom: "24px" }}>
                  <StatusBanner icon={CheckCircle2} tone="green">
                    Thank you. Your feedback for{" "}
                    <strong>{state.cycle.cycle_name}</strong> has already been
                    submitted.
                  </StatusBanner>
                </div>

              </div>
            ) : null}


            {state.cycle && !state.existingFeedback ? (
              <form style={card} onSubmit={handleSubmit}>
                <div
                  style={{
                    marginBottom: "20px",
                    borderBottom: "1px solid #dfe8ee",
                    paddingBottom: "20px",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.16em",
                        color: "#028D93",
                      }}
                    >
                      Active Cycle
                    </p>
                    <h2 style={{ marginTop: "8px", fontSize: "20px", fontWeight: 700, color: "#00293A" }}>
                      {state.cycle.cycle_name}
                    </h2>
                  </div>
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: "8px",
                      borderRadius: "9999px",
                      background: "#d9f3f2",
                      padding: "4px 12px",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#028D93",
                    }}
                  >
                    {completedCount} of 5 questions completed
                  </span>
                  <div
                    style={{
                      marginTop: "16px",
                      height: "8px",
                      overflow: "hidden",
                      borderRadius: "9999px",
                      background: "#f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: "9999px",
                        background: "#028D93",
                        transition: "width 0.3s ease",
                        width: `${progressPercent}%`,
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gap: "16px" }}>
                  <RatingInput
                    index="1"
                    label="How helpful was your mentor's academic guidance?"
                    name="q1_rating"
                    onChange={(value) =>
                      setRatings((current) => ({
                        ...current,
                        q1_rating: value,
                      }))
                    }
                    value={ratings.q1_rating}
                  />
                  <RatingInput
                    index="2"
                    label="How responsive and approachable was your mentor?"
                    name="q2_rating"
                    onChange={(value) =>
                      setRatings((current) => ({
                        ...current,
                        q2_rating: value,
                      }))
                    }
                    value={ratings.q2_rating}
                  />
                  <RatingInput
                    index="3"
                    label="Did the mentor share useful resources and materials?"
                    name="q3_rating"
                    onChange={(value) =>
                      setRatings((current) => ({
                        ...current,
                        q3_rating: value,
                      }))
                    }
                    value={ratings.q3_rating}
                  />
                  <RatingInput
                    index="4"
                    label="How effective was the mentor in resolving your queries?"
                    name="q4_rating"
                    onChange={(value) =>
                      setRatings((current) => ({
                        ...current,
                        q4_rating: value,
                      }))
                    }
                    value={ratings.q4_rating}
                  />
                  <RatingInput
                    index="5"
                    label="Rate the overall impact of the mentorship program."
                    name="q5_rating"
                    onChange={(value) =>
                      setRatings((current) => ({
                        ...current,
                        q5_rating: value,
                      }))
                    }
                    value={ratings.q5_rating}
                  />
                </div>

                {submitError ? (
                  <div style={{ marginTop: "20px" }}>
                    <StatusBanner tone="red">{submitError}</StatusBanner>
                  </div>
                ) : null}

                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    style={{ minWidth: "208px" }}
                    disabled={!canSubmit}
                    icon={Send}
                    isLoading={isSubmitting}
                    type="submit"
                  >
                    Submit feedback
                  </Button>
                </div>
              </form>
            ) : null}

            {chartData.length > 0 && (
              <div style={card}>
                <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#00293A", marginBottom: "16px" }}>Feedback History</h3>
                <div style={{ height: "300px", width: "100%", paddingRight: "20px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 5]} stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickCount={6} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        formatter={(value) => [`${value} / 5`, 'Average Rating']}
                      />
                      <Line type="monotone" dataKey="rating" stroke="#028D93" strokeWidth={3} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </section>
        </div>
      ) : null}
    </PageShell>
  );
}
